// admin-analytics-expansion P2a (D4/D5) — Ticket Browse API client
//
// browseTickets(params) → GET /admin/tickets (org_admin | supervisor)
//
// Mirrors the shape of ticket-analytics.ts (buildQueryString + runtime-
// validated response via the shared Zod schema).

import type { TicketBrowseQuery, TicketBrowseResponse } from '@jq4race/shared';
import { TicketBrowseResponseSchema } from '@jq4race/shared';

import { request } from './client.js';

function buildQueryString(params: TicketBrowseQuery): string {
  const entries: [string, string][] = [];
  if (params.locationId) entries.push(['locationId', params.locationId]);
  if (params.status) entries.push(['status', params.status]);
  if (params.paymentStatus) entries.push(['paymentStatus', params.paymentStatus]);
  if (params.from) entries.push(['from', params.from]);
  if (params.to) entries.push(['to', params.to]);
  if (params.cursor) entries.push(['cursor', params.cursor]);
  if (params.limit !== undefined) entries.push(['limit', String(params.limit)]);
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}`;
}

/**
 * GET /admin/tickets
 *
 * Returns a cursor-paginated, filterable list of tickets. Server-enforced
 * least-privilege: supervisors omitting `locationId` are auto-scoped to
 * their own assigned location(s); an explicit `locationId` outside their
 * assignment set is rejected with 403.
 */
export async function browseTickets(params: TicketBrowseQuery = {}): Promise<TicketBrowseResponse> {
  const payload = await request<unknown>(`/admin/tickets${buildQueryString(params)}`, {
    method: 'GET',
  });
  return TicketBrowseResponseSchema.parse(payload);
}
